import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, Search, Pencil, Trash2, X, Save, RefreshCw, Star, Percent } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import ImagePasteField from './ImagePasteField';
import BulkPricePanel from './BulkPricePanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  cat: '', name: '', strength: '', packaging: '',
  price: '', image: '', desc: '', isRx: true, isTop: false, badge: '',
};

const BADGE_OPTIONS = [
  { id: '',           label: 'None' },
  { id: 'bestseller', label: 'Bestseller' },
  { id: 'new',        label: 'New' },
  { id: 'sale',       label: 'Sale' },
];

const FormRow = ({ label, required, children }) => (
  <div>
    <label className="block text-[10.5px] tracking-[0.12em] text-neutral-500 uppercase mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const inputCls = "w-full bg-white dark:bg-[#0f0f10] border border-neutral-200 dark:border-neutral-800 rounded-md px-3 py-2.5 text-[14px] text-neutral-900 dark:text-neutral-100 outline-none focus:border-neutral-400 dark:focus:border-neutral-600";

const ProductFormModal = ({ initial, onClose, onSave, saving, catOptions = [] }) => {
  const [form, setForm] = useState(initial);
  const [err, setErr] = useState('');
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = () => {
    if (!form.name.trim()) return setErr('Name is required');
    const price = parseFloat(form.price);
    if (Number.isNaN(price) || price < 0) return setErr('Valid price is required');
    onSave({ ...form, price });
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="relative bg-white dark:bg-[#141416] rounded-2xl w-full max-w-2xl my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 dark:border-neutral-800">
          <h3 className="font-serif text-[22px] text-neutral-900 dark:text-neutral-100">
            {initial.id ? 'Edit product' : 'Add product'}
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full border border-neutral-200 dark:border-neutral-700 flex items-center justify-center hover:bg-neutral-50 dark:hover:bg-neutral-800"><X size={15} /></button>
        </div>

        <div className="p-6 space-y-4">
          <FormRow label="Product name" required>
            <input className={inputCls} value={form.name} onChange={(e) => upd('name', e.target.value)} placeholder="e.g. Metformin" />
          </FormRow>

          <div className="grid grid-cols-2 gap-4">
            <FormRow label="Category" required>
              <select className={inputCls} value={form.cat} onChange={(e) => upd('cat', e.target.value)}>
                {catOptions.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </FormRow>
            <FormRow label="Price (USD)" required>
              <input type="number" step="0.01" min="0" className={inputCls} value={form.price} onChange={(e) => upd('price', e.target.value)} placeholder="e.g. 14.00" />
            </FormRow>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormRow label="Strength">
              <input className={inputCls} value={form.strength} onChange={(e) => upd('strength', e.target.value)} placeholder="e.g. 500mg" />
            </FormRow>
            <FormRow label="Packaging">
              <input className={inputCls} value={form.packaging} onChange={(e) => upd('packaging', e.target.value)} placeholder="e.g. 10 x 10 or 100 tabs" />
            </FormRow>
          </div>

          <FormRow label="Product image">
            <ImagePasteField value={form.image} onChange={(v) => upd('image', v)} />
          </FormRow>

          <FormRow label="Description">
            <textarea rows={3} className={inputCls} value={form.desc} onChange={(e) => upd('desc', e.target.value)} placeholder="What it does, safety notes…" />
          </FormRow>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormRow label="Featured badge">
              <select
                data-testid="product-badge-select"
                value={form.badge || ''}
                onChange={(e) => upd('badge', e.target.value)}
                className={inputCls}
              >
                {BADGE_OPTIONS.map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
              </select>
            </FormRow>
            <div className="flex items-center gap-6 sm:pt-6">
              <label className="inline-flex items-center gap-2 text-[13.5px] cursor-pointer">
                <input type="checkbox" checked={form.isRx} onChange={(e) => upd('isRx', e.target.checked)} className="w-4 h-4 accent-neutral-800" />
                Prescription (Rx)
              </label>
              <label className="inline-flex items-center gap-2 text-[13.5px] cursor-pointer">
                <input type="checkbox" checked={form.isTop} onChange={(e) => upd('isTop', e.target.checked)} className="w-4 h-4 accent-neutral-800" />
                Top Seller
              </label>
            </div>
          </div>

          {err && <p className="text-red-600 text-[13px]">{err}</p>}
        </div>

        <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-800 flex justify-end gap-2">
          <button onClick={onClose} className="border border-neutral-200 dark:border-neutral-700 rounded-md px-4 py-2 text-[13.5px] hover:bg-neutral-50 dark:hover:bg-neutral-800">Cancel</button>
          <button onClick={submit} disabled={saving} className={`inline-flex items-center gap-1.5 rounded-md px-5 py-2 text-[13.5px] text-white ${saving ? 'bg-neutral-400 cursor-not-allowed' : 'bg-[#1e293b] hover:bg-[#0f172a]'}`}>
            <Save size={14} /> {saving ? 'Saving…' : (initial.id ? 'Save changes' : 'Create product')}
          </button>
        </div>
      </div>
    </div>
  );
};

const ProductsPanel = () => {
  const { password } = useAdminAuth();
  const headers = useMemo(() => ({ 'X-Admin-Password': password }), [password]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [query, setQuery] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [editing, setEditing] = useState(null); // null | {form-values}
  const [saving, setSaving] = useState(false);
  const [bsStatus, setBsStatus] = useState(null);   // { last_run, last_top, next_scheduled }
  const [bsBusy, setBsBusy] = useState(false);
  const [bsMsg, setBsMsg] = useState('');
  const [catOptions, setCatOptions] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);

  const loadCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/categories`, { headers });
      const opts = (data || []).map(c => ({ id: c.id, label: c.display || c.label || c.id }));
      setCatOptions(opts);
      setEditing(prev => (prev && !prev.cat && opts.length ? { ...prev, cat: opts[0].id } : prev));
    } catch { /* silent — categories may be empty */ }
  }, [headers]);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const { data } = await axios.get(`${API}/products`);
      setProducts(data || []);
    } catch { setErr('Failed to load products'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadBestsellerStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/bestsellers/status`, { headers });
      setBsStatus(data);
    } catch { /* silent */ }
  }, [headers]);

  useEffect(() => { loadBestsellerStatus(); }, [loadBestsellerStatus]);

  const refreshBestsellers = async () => {
    setBsBusy(true); setBsMsg('');
    try {
      const { data } = await axios.post(`${API}/admin/bestsellers/refresh`, {}, { headers });
      setBsStatus(prev => ({ ...(prev || {}), ...data }));
      setBsMsg(`Tagged ${data.tagged} · Untagged ${data.untagged} · Top ${data.top_ids?.length || 0} products`);
      await load();
    } catch (e) {
      setBsMsg('Refresh failed: ' + (e?.response?.data?.detail || e.message));
    } finally { setBsBusy(false); }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
  };

  const filtered = useMemo(() => {
    let list = products;
    if (catFilter !== 'all') list = list.filter(p => p.cat === catFilter);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(q) || (p.strength || '').toLowerCase().includes(q));
    }
    return list;
  }, [products, query, catFilter]);

  const save = async (data) => {
    setSaving(true);
    try {
      if (editing.id) {
        await axios.patch(`${API}/products/${editing.id}`, data, { headers });
      } else {
        await axios.post(`${API}/products`, data, { headers });
      }
      setEditing(null);
      await load();
    } catch (e) {
      alert('Failed to save: ' + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name} (${p.strength})?`)) return;
    try {
      await axios.delete(`${API}/products/${p.id}`, { headers });
      await load();
    } catch (e) { alert('Failed to delete: ' + (e?.response?.data?.detail || e.message)); }
  };

  return (
    <div className="space-y-4">
      {/* Actions bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name or strength…" className="w-full bg-white dark:bg-[#141416] border border-neutral-200 dark:border-neutral-800 rounded-md pl-9 pr-3 py-2.5 text-[14px] outline-none focus:border-neutral-400" />
        </div>
        <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="bg-white dark:bg-[#141416] border border-neutral-200 dark:border-neutral-800 rounded-md px-3 py-2.5 text-[13.5px]">
          <option value="all">All categories</option>
          {catOptions.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <button onClick={load} className="inline-flex items-center gap-1.5 border border-neutral-200 dark:border-neutral-700 rounded-md px-3 py-2.5 text-[13.5px] hover:bg-neutral-50 dark:hover:bg-neutral-800"><RefreshCw size={14} /> Refresh</button>
        <button
          data-testid="refresh-bestsellers-btn"
          onClick={refreshBestsellers}
          disabled={bsBusy}
          title="Auto-tag the top 10 most-ordered products (30-day window) as Bestseller"
          className="inline-flex items-center gap-1.5 border border-amber-400 text-amber-700 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-500/50 rounded-md px-3 py-2.5 text-[13.5px] hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-60"
        ><Star size={14} strokeWidth={2.5} /> {bsBusy ? 'Refreshing…' : 'Refresh bestsellers'}</button>
        <button data-testid="bulk-price-toggle-btn" onClick={() => setBulkOpen(o => !o)} className="inline-flex items-center gap-1.5 border border-emerald-500 text-emerald-700 dark:text-emerald-300 rounded-md px-3 py-2.5 text-[13.5px] hover:bg-emerald-50 dark:hover:bg-emerald-950/30"><Percent size={14} /> Bulk price</button>
        <button onClick={() => setEditing(emptyForm)} className="inline-flex items-center gap-1.5 bg-[#1e293b] hover:bg-[#0f172a] text-white rounded-md px-4 py-2.5 text-[13.5px]"><Plus size={15} /> Add product</button>
      </div>

      {bulkOpen && <BulkPricePanel catOptions={catOptions} onClose={() => setBulkOpen(false)} onApplied={load} />}

      {/* Auto-bestseller status card */}
      {(bsStatus || bsMsg) && (
        <div
          data-testid="bestseller-status"
          className="rounded-lg border border-amber-200 dark:border-amber-800/60 bg-amber-50/60 dark:bg-amber-950/20 px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12.5px]"
        >
          <div className="inline-flex items-center gap-1.5 text-amber-800 dark:text-amber-300 font-medium">
            <Star size={12} strokeWidth={2.5} /> Auto-Bestseller
          </div>
          <div className="text-neutral-600 dark:text-neutral-300">
            Last run: <span data-testid="bestseller-last-run" className="text-neutral-900 dark:text-neutral-100">{fmtDate(bsStatus?.last_run)}</span>
          </div>
          <div className="text-neutral-600 dark:text-neutral-300">
            Next run: <span className="text-neutral-900 dark:text-neutral-100">{fmtDate(bsStatus?.next_scheduled)}</span>
          </div>
          <div className="text-neutral-600 dark:text-neutral-300">
            Window: <span className="text-neutral-900 dark:text-neutral-100">{bsStatus?.window_days || 30}d</span> · Limit <span className="text-neutral-900 dark:text-neutral-100">{bsStatus?.limit || 10}</span>
          </div>
          {bsStatus?.last_top?.length > 0 && (
            <div className="text-neutral-500 w-full basis-full min-w-0">
              Top IDs: <span data-testid="bestseller-top-ids" className="font-mono text-[11.5px] text-neutral-700 dark:text-neutral-300 break-all">{bsStatus.last_top.join(', ')}</span>
            </div>
          )}
          {bsMsg && <div data-testid="bestseller-msg" className="text-emerald-700 dark:text-emerald-400 text-[12px]">{bsMsg}</div>}
        </div>
      )}

      {err && <div className="text-red-600 text-[13px]">{err}</div>}
      {loading && products.length === 0 && <div className="py-16 text-center text-neutral-500 text-[13.5px]">Loading products…</div>}
      {!loading && filtered.length === 0 && <div className="py-16 text-center text-neutral-500 text-[13.5px]">No products match your filters.</div>}

      {/* Table */}
      {filtered.length > 0 && (
        <div className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] overflow-hidden">
          <div className="grid grid-cols-[60px_1.6fr_1.3fr_1fr_0.6fr_0.6fr_100px] gap-3 px-4 py-3 text-[11px] tracking-widest uppercase text-neutral-500 border-b border-neutral-200 dark:border-neutral-800">
            <div>Image</div><div>Name</div><div>Category</div><div>Strength / Pack</div><div>Price</div><div>Flags</div><div>Actions</div>
          </div>
          {filtered.slice(0, 200).map(p => (
            <div key={p.id} className="grid grid-cols-[60px_1.6fr_1.3fr_1fr_0.6fr_0.6fr_100px] gap-3 px-4 py-3 items-center border-b border-neutral-100 dark:border-neutral-800 last:border-b-0">
              <div className="w-10 h-10 rounded bg-neutral-50 dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 overflow-hidden flex items-center justify-center">
                {p.image ? <img src={p.image} alt={p.name} className="max-h-full max-w-full object-cover" onError={(e) => { e.currentTarget.style.opacity = 0.2; }} /> : <span className="text-[10px] text-neutral-400">n/a</span>}
              </div>
              <div className="min-w-0">
                <div className="text-[14px] font-medium text-neutral-900 dark:text-neutral-100 truncate">{p.name}</div>
                <div className="text-[11px] text-neutral-500">ID {p.id}</div>
              </div>
              <div className="text-[13px] text-neutral-700 dark:text-neutral-300 truncate">{p.catLabel}</div>
              <div className="text-[12.5px] text-neutral-600 dark:text-neutral-300 truncate">{p.strength}{p.packaging ? ' · ' + p.packaging : ''}</div>
              <div className="text-[14px] font-medium text-neutral-900 dark:text-neutral-100">${Number(p.price).toFixed(2)}</div>
              <div className="flex gap-1 flex-wrap">
                {p.isRx && <span className="text-[10px] px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300">Rx</span>}
                {p.isTop && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">TOP</span>}
                {p.badge === 'bestseller' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500 text-white">Best</span>}
                {p.badge === 'new' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-600 text-white">New</span>}
                {p.badge === 'sale' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500 text-white">Sale</span>}
              </div>
              <div className="flex justify-end gap-1">
                <button onClick={() => setEditing({ ...p, price: String(p.price) })} className="p-1.5 text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100" title="Edit"><Pencil size={15} /></button>
                <button onClick={() => del(p)} className="p-1.5 text-neutral-400 hover:text-red-500" title="Delete"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
          {filtered.length > 200 && (
            <div className="px-4 py-3 text-center text-[12px] text-neutral-500 border-t border-neutral-100 dark:border-neutral-800">
              Showing 200 of {filtered.length} — use search or filter to narrow down.
            </div>
          )}
        </div>
      )}

      {editing && (
        <ProductFormModal initial={editing} onClose={() => setEditing(null)} onSave={save} saving={saving} catOptions={catOptions} />
      )}
    </div>
  );
};

export default ProductsPanel;
